/**
 * AR Technical Demo (F1 / GEN-2257) — Scene 2 reconciliation confirmation +
 * flagged-issues callout.
 *
 * THROWAWAY DEMO CODE. The storyboard's Scene 2 shows the matched remittance
 * line items AND the two deductions the remittance carried, flagged with their
 * reasons. The real `RemittanceLineItemsTable` has no notion of deductions
 * (deductions surface on the deduction thread, F2's surface), so rather than
 * alter that shared component we render a small demo-only callout beneath the
 * line items, fed from the fixture store's `deductions` slice. Mounted only from
 * `RemittanceDetails` behind an `isArDemoMode()` guard, scoped to the deductions
 * taken against this remittance's invoices.
 *
 * Gated on `technicalUi.remittanceProcessed` so it only appears once the agent
 * runs the Scene-2 "process this remittance" beat — and when it does, the
 * reconciliation confirmation and each flagged issue reveal one-by-one (shared
 * `evidenceStyles` fly-in) so the agent's work visibly "happens". Delete with
 * src/demo/ar.
 */

import { T, Var } from 'gt-react';
import { useReducedMotion } from 'framer-motion';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Currency } from '@/components/common/Currency';
import { cn } from '@/lib/utils';
import { useArDemoStore } from './fixture-store';
import { getArDemoTool } from './demo-script';
import type { ArDemoFixtureState, DemoDeduction } from './fixture-store.types';
import { evidenceStyles, REVEAL_CLASS } from './demo-animations';

/**
 * Resolve the checkpoint + navigation tools that open a flagged deduction's
 * collection thread. These are the SAME tools the scripted Nami beats and the
 * summary's "Today's work" rows fire, so clicking a flagged issue lands on the
 * thread with the right fixture state applied (the checkpoint "opens" the
 * thread, the nav tool jumps to it). Empty when the deduction isn't tied to a
 * known thread, in which case the card stays non-interactive.
 */
function threadToolsForDeduction(
  deduction: DemoDeduction,
  technical: ArDemoFixtureState['technical'],
): string[] {
  if (deduction.threadId === technical.shortPayThreadId) {
    return [
      'ar_tech_checkpoint_applyShortPayThreadOpened',
      'ar_tech_navigate_short_pay_thread',
    ];
  }
  if (deduction.threadId === technical.deductionThreadId) {
    return [
      'ar_tech_checkpoint_applyDeductionThreadOpened',
      'ar_tech_navigate_deduction_thread',
    ];
  }
  return [];
}

/**
 * Run a deduction's thread tools in order (checkpoint then navigate), resolved
 * by name against the demo-script registry. Mirrors the summary task list's
 * `runTaskTools`; a missing tool is skipped with a warn so the row degrades.
 */
async function runDeductionThreadTools(toolNames: string[]): Promise<void> {
  for (const name of toolNames) {
    const tool = getArDemoTool(name);
    if (!tool) {
      console.warn('[ar-demo] deduction callout references missing tool', name);
      continue;
    }
    await tool.execute(undefined as never);
  }
}

// Stagger: the reconciliation confirmation eases in slowly first, then the
// issues header, then each disputed card — so "processing" reads deliberately.
const CONFIRM_DURATION_MS = 1100;
const HEADER_DELAY_MS = 1200;
const FIRST_CARD_DELAY_MS = 1450;
const CARD_STEP_MS = 220;

/**
 * Render the Scene-2 reconciliation confirmation + the deductions flagged
 * against the given remittance's invoices. Returns null until the agent has
 * processed the remittance (and when there are no deductions, so non-Crestline
 * demo remittances show nothing).
 */
export function DemoRemittanceDeductionsCallout({
  invoiceIds,
}: {
  invoiceIds: string[];
}) {
  const reduce = useReducedMotion();
  // Only surface the flagged issues once the agent has processed the remittance
  // (Scene 2). Cold open / direct navigation shows the raw advice with no
  // analysis, so "process this remittance" is what makes the discrepancies appear.
  const processed = useArDemoStore((s) => s.technicalUi.remittanceProcessed);
  const deductions = useArDemoStore((s) =>
    s.deductions.filter((d) => invoiceIds.includes(d.invoiceId)),
  );
  // Thread ids used to map each flagged deduction to its collection thread, so
  // clicking the card navigates there the way the summary's task list does.
  const technical = useArDemoStore((s) => s.technical);

  if (!processed || deductions.length === 0) return null;

  // Re-mount the animation each time processing flips on by keying off the flag.
  const revealClass = reduce ? undefined : REVEAL_CLASS;

  return (
    <div key={String(processed)} className="space-y-2">
      <style>{evidenceStyles}</style>

      {/* Reconciliation confirmation — lands first. */}
      <div
        className={cn(
          'flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm font-medium text-emerald-900',
          revealClass,
        )}
        style={
          reduce
            ? undefined
            : {
                animationDelay: '0ms',
                animationDuration: `${CONFIRM_DURATION_MS}ms`,
              }
        }
      >
        <CheckCircleIcon className="size-4 shrink-0 text-emerald-600" />
        <T context="AR demo: remittance reconciled confirmation">
          Reconciled against <Var>{invoiceIds.length}</Var> open invoices and
          contract terms.
        </T>
      </div>

      <div
        className={cn(
          'flex items-center gap-1.5 text-sm font-semibold',
          revealClass,
        )}
        style={reduce ? undefined : { animationDelay: `${HEADER_DELAY_MS}ms` }}
      >
        <WarningCircleIcon className="size-4 text-amber-600" />
        <T context="AR demo: issues flagged on the remittance">
          Issues flagged (<Var>{deductions.length}</Var>)
        </T>
      </div>
      <div className="space-y-2">
        {deductions.map((deduction, i) => {
          const threadTools = threadToolsForDeduction(deduction, technical);
          const isClickable = threadTools.length > 0;
          return (
            <button
              key={deduction.id}
              type="button"
              disabled={!isClickable}
              onClick={
                isClickable
                  ? () => void runDeductionThreadTools(threadTools)
                  : undefined
              }
              className={cn(
                'group block w-full rounded border border-amber-200 bg-amber-50/60 px-3 py-2 text-left transition-colors',
                isClickable &&
                  'cursor-pointer hover:border-amber-400 hover:bg-amber-100/70',
                revealClass,
              )}
              style={
                reduce
                  ? undefined
                  : {
                      animationDelay: `${FIRST_CARD_DELAY_MS + i * CARD_STEP_MS}ms`,
                    }
              }
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-amber-300 bg-amber-100 text-amber-900"
                  >
                    {deduction.code}
                  </Badge>
                  <span className="text-sm font-medium">
                    {deduction.invoiceNumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Currency
                    value={deduction.claimedAmount}
                    className="text-sm font-semibold text-amber-900"
                    fallback="--"
                  />
                  {isClickable && (
                    <CaretRightIcon
                      className="size-4 shrink-0 text-amber-700/40 transition-colors group-hover:text-amber-700"
                      aria-hidden
                    />
                  )}
                </div>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {deduction.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
